import moment from 'moment'
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import axios from '~/data/http/axios'
import AddCard from '~/Common-Objects/UI/Payment/addCardStripe'
import { alterSiteDrawerWidth } from '~/data/actions/siteActions'
import SiteVars from '~/Classes/siteVars'
import i18n from '~/i18n'

function TermsParagraphs({ translationKey }) {
    return i18n.t(translationKey).split('\n\n').map((paragraph, index, paragraphs) => (
        <React.Fragment key={translationKey + index}>
            {paragraph}
            {index < paragraphs.length - 1 && (
                <>
                    <br />
                    <br />
                </>
            )}
        </React.Fragment>
    ))
}

export default function SmsUpgrade({ onFinish = () => {} }) {
    const dispatch = useDispatch()
    const [account, setAccount] = useState()
    const [plans, setPlans] = useState()
    const [stripeCustomer, setStripeCustomer] = useState()
    const [newPlan, setNewPlan] = useState()
    const [currentPlan, setCurrentPlan] = useState()
    const [settingDefault, setSettingsDefault] = useState(false)
    const [changingPlan, setChangingPlan] = useState(false)
    const [calculated, setCalculated] = useState()
    const [cancelled, setCancelled] = useState(false)
    const [stripe, setStripe] = useState()
    const [drawer, setDrawer] = useState()

    const getPlans = async () => {
        const res = await axios.get('/accountMaster/plan?type=2')
        setPlans(res.data.Plans)
    }

    const getUsage = async () => {
        const res = await axios.get('/accountMaster/usage')
        if (res.data.NextSmsPlan?.id === -1) setCancelled(true)
        setNewPlan(res.data.SmsPlan.id)
    }

    const getAccount = async () => {
        const res = await axios.get('/accountMaster')
        setAccount(res.data.AccountMaster)
        setCurrentPlan(plans?.find((p) => p.id === res.data.AccountMaster.SmsAccountBillingId))
    }

    const getStripe = async () => {
        const res = await axios.get('/accountMaster/paymentMethod/stripe')
        setStripeCustomer(res.data.customer)
    }

    const setDefault = async (id) => {
        setSettingsDefault(true)
        try {
            await axios.put('/accountMaster/paymentMethod/stripe', {
                default_source: id
            })
            await getStripe()
        } catch (e) {}
        setSettingsDefault(false)
    }

    const changePlan = async () => {
        setChangingPlan(true)
        try {
            await axios.put('/accountMaster/plan/sms', {
                planId: newPlan
            })
            onFinish()
        } catch (e) {}
        setChangingPlan(false)
    }

    const cancelSubscription = async () => {
        setChangingPlan(true)
        try {
            await axios.delete('/accountMaster/plan/sms')
            onFinish()
        } catch (e) {}
        setChangingPlan(false)
    }

    const resumeSubscription = async () => {
        setChangingPlan(true)
        try {
            await axios.delete('/accountMaster/plan/sms?undo=true')
            onFinish()
        } catch (e) {}
        setChangingPlan(false)
    }

    const getPredictedPrice = async () => {
        setCalculated(null)
        try {
            const res = await axios.get('/accountMaster/plan/sms?planId=' + newPlan)
            setCalculated(res.data)
        } catch (e) {}
    }

    const checkStripe = async () => {
        if (!stripe) {
            if (window.Stripe) {
                setStripe(Stripe(SiteVars.stripeKey))
            } else {
                await new Promise((resolve, reject) => {
                    setTimeout(resolve, 300)
                })
                checkStripe()
            }
        }
    }

    const addCard = () => {
        const content = (
            <AddCard
                stripe={stripe}
                close={async () => {
                    await getStripe()

                    dispatch(alterSiteDrawerWidth(500))
                    setDrawer(null)
                }}
            />
        )
        dispatch(alterSiteDrawerWidth('100vw'))
        setDrawer(content)
    }

    useEffect(() => {
        getPlans()
        getStripe()
        checkStripe()
    }, [])

    useEffect(() => {
        if (plans?.length) {
            getAccount()
            getUsage()
        }
    }, [plans])

    useEffect(() => {
        if (newPlan > 0) {
            getPredictedPrice()
        }
    }, [newPlan])

    if (!account || !plans || !currentPlan || !stripeCustomer) return <LoadingIcons />

    const planOptions = [
        { label: i18n.t('company:sms.cancelOption'), value: -1 },
        ...plans.map((p) => ({
            label: `${p.symbol}${p.rawPrice} (${FormatNumberFuncs.formatNumber(p.maxUniqueSends)} SMS Credits)`,
            value: p.id
        }))
    ]

    const newPlanDetails = plans.find((p) => p.id === newPlan)
    const isUpgrade =
        calculated && calculated?.currentPlan.price < calculated?.newPlan.price && newPlan !== currentPlan.id
    const newPrice = calculated ? calculated.cost * 1.2 : undefined // for VAT
    const isAnnual = account.billingFrequency === 1

    if (drawer) return drawer

    return (
        <>
            <Heading style={{ marginBottom: 8 }}>{i18n.t('company:sms.title')}</Heading>
            <SubHeading style={{ marginBottom: 24 }}>{i18n.t('company:sms.subtitle')}</SubHeading>
            <Select
                outerClassName='mar-b24'
                data={planOptions}
                value={newPlan}
                onChange={(e) => setNewPlan(e.target.value)}
                label={i18n.t('company:sms.monthlyCredits')}
            />
            {newPlan > 0 && !!calculated && (
                <>
                    <CostBlock
                        addVat={account.addVAT}
                        symbol={currentPlan.symbol}
                        currentPlan={calculated.currentPlan}
                        newPlan={calculated.newPlan}
                        cost={calculated.cost}
                        billingFrequency={account.billingFrequency}
                    />
                    {newPlanDetails?.price > currentPlan.price && newPlan !== currentPlan.id && (
                        <>
                            <Select
                                label={i18n.t('company:sms.paymentMethod')}
                                value={stripeCustomer.default_source}
                                data={(stripeCustomer.sources.data || []).map((source) => ({
                                    label: `${source.brand} ending in ${source.last4} (exp. ${source.exp_month}/${source.exp_year})`,
                                    value: source.id
                                }))}
                                iconRight={settingDefault ? <Icons icon='loading' /> : null}
                                disabled={settingDefault}
                                onChange={(e) => setDefault(e.target.value)}
                                outerClassName='mar-b24'
                                labelOptions={<a onClick={addCard}>{i18n.t('company:sms.addPaymentMethod')}</a>}
                            />
                        </>
                    )}
                    {newPlan !== currentPlan.id && (
                        <>
                            <Button
                                className='button-primary mar-b24'
                                onClick={changePlan}
                                disabled={changingPlan}
                            >
                                {isUpgrade && (
                                    <>
                                        {i18n.t('company:sms.payToUpgrade', {
                                            symbol: currentPlan.symbol,
                                            amount: FormatNumberFuncs.formatNumber(newPrice, 2)
                                        })}
                                    </>
                                )}
                                {!isUpgrade && (
                                    <>
                                        {i18n.t('company:sms.downgradeTo', {
                                            symbol: currentPlan.symbol,
                                            amount: FormatNumberFuncs.formatNumber(
                                                account.billingFrequency === 0
                                                    ? newPlanDetails.price
                                                    : newPlanDetails.price * 12,
                                                2
                                            ),
                                            period: account.billingFrequency === 0 ? i18n.t('company:sms.month') : i18n.t('company:sms.year')
                                        })}
                                    </>
                                )}
                            </Button>
                            {isUpgrade && (
                                <>
                                    <SubHeading>
                                        {!isAnnual && <TermsParagraphs translationKey='company:sms.termsUpgradeMonthly' />}
                                        {isAnnual && <TermsParagraphs translationKey='company:sms.termsUpgradeAnnual' />}
                                    </SubHeading>
                                </>
                            )}
                            {!isUpgrade && (
                                <>
                                    <SubHeading>
                                        {!isAnnual && <TermsParagraphs translationKey='company:sms.termsDowngradeMonthly' />}
                                        {isAnnual && <TermsParagraphs translationKey='company:sms.termsDowngradeAnnual' />}
                                    </SubHeading>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
            {cancelled && currentPlan.id === newPlan && (
                <Button className='button-primary' onClick={resumeSubscription}>
                    {i18n.t('company:sms.resumeSubscription')}
                </Button>
            )}
            {newPlan > 0 && !calculated && <LoadingIcons />}
            {account.nextSmsAccountBillingId > 0 && currentPlan.id === newPlan && (
                <>
                    <Button className='button-primary' onClick={resumeSubscription}>
                        {i18n.t('company:sms.resumeSubscription')}
                    </Button>
                </>
            )}
            {newPlan === -1 && (
                <>
                    <Button className='button-red mar-b24' onClick={cancelSubscription} disabled={changingPlan}>
                        {i18n.t('company:sms.cancelSubscription')}
                    </Button>
                    <SubHeading>
                        {isAnnual && <TermsParagraphs translationKey='company:sms.termsCancelAnnual' />}
                        {!isAnnual && <TermsParagraphs translationKey='company:sms.termsCancelMonthly' />}
                    </SubHeading>
                </>
            )}
        </>
    )
}

function Heading({ children, style }) {
    return (
        <div
            style={{
                fontWeight: 600,
                fontStyle: 'Semi Bold',
                fontSize: '16px',
                leadingTrim: 'NONE',
                lineHeight: '24px',
                letterSpacing: '-0.24px',
                color: 'var(--neutral-950)',
                ...style
            }}
        >
            {children}
        </div>
    )
}

function SubHeading({ children, style }) {
    return (
        <div
            style={{
                fontWeight: 400,
                fontStyle: 'Regular',
                fontSize: '13px',
                leadingTrim: 'NONE',
                lineHeight: '20px',
                letterSpacing: '-0.1px',
                color: 'var(--neutral-800)',
                ...style
            }}
        >
            {children}
        </div>
    )
}

function CostBlock({ addVat, symbol, currentPlan, newPlan, cost, billingFrequency = 0 }) {
    let currentPrice = billingFrequency === 1 ? currentPlan.annualPrice : currentPlan.price
    return (
        <Paper className='paper-padding-24'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Heading>{i18n.t('company:sms.summary')}</Heading>
                {currentPlan?.id === newPlan?.id && (
                    <>
                        <SummaryRow
                            currency={symbol}
                            label={billingFrequency === 1 ? i18n.t('company:sms.priceAnnually') : i18n.t('company:sms.price')}
                            value={currentPrice}
                        />
                        {addVat && <SummaryRow currency={symbol} label={i18n.t('company:sms.vat')} value={currentPrice * 0.2} />}
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.total')} value={currentPrice * (addVat ? 1.2 : 1)} />
                    </>
                )}
                {currentPlan.price < newPlan?.price && currentPlan?.id !== newPlan?.id && billingFrequency === 0 && (
                    <>
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.previousMonthlyPrice')} value={currentPlan.price} />
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.newMonthlyPrice')} value={newPlan.price} />
                        <SummaryRow
                            currency={symbol}
                            label={i18n.t('company:sms.proRataMonth', { month: moment().format('MMMM') })}
                            value={newPlan.price - currentPlan.price}
                        />
                        {addVat && (
                            <SummaryRow
                                currency={symbol}
                                label={i18n.t('company:sms.vat')}
                                value={(newPlan.price - currentPlan.price) * 0.2}
                            />
                        )}
                        <SummaryRow
                            currency={symbol}
                            label={i18n.t('company:sms.total')}
                            value={(newPlan.price - currentPlan.price) * (addVat ? 1.2 : 1)}
                        />
                    </>
                )}
                {currentPlan.price < newPlan?.price && currentPlan?.id !== newPlan?.id && billingFrequency === 1 && (
                    <>
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.newAnnualPrice')} value={newPlan.annualPrice} />
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.proRataPrice')} value={newPlan.annualPrice - cost} />
                        {addVat && <SummaryRow currency={symbol} label={i18n.t('company:sms.vat')} value={cost * 0.2} />}
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.total')} value={cost * (addVat ? 1.2 : 1)} />
                    </>
                )}
                {currentPlan.price > newPlan?.price && currentPlan?.id !== newPlan?.id && billingFrequency === 0 && (
                    <>
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.previousPrice')} value={currentPlan.price} />
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.newPrice')} value={newPlan.price} />
                        {addVat && <SummaryRow currency={symbol} label={i18n.t('company:sms.vat')} value={newPlan.price * 0.2} />}
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.total')} value={newPlan.price * (addVat ? 1.2 : 1)} />
                    </>
                )}
                {currentPlan.price > newPlan?.price && currentPlan?.id !== newPlan?.id && billingFrequency === 1 && (
                    <>
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.previousPrice')} value={currentPrice} />
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.newPrice')} value={newPlan.annualPrice} />
                        {addVat && <SummaryRow currency={symbol} label={i18n.t('company:sms.vat')} value={newPlan.annualPrice * 0.2} />}
                        <SummaryRow currency={symbol} label={i18n.t('company:sms.total')} value={newPlan.annualPrice * (addVat ? 1.2 : 1)} />
                    </>
                )}
            </div>
        </Paper>
    )
}

function SummaryRow({ label, currency, value }) {
    const spanStyle = {
        fontWeight: 400,
        fontStyle: 'Regular',
        fontSize: '14px',
        leadingTrim: 'NONE',
        lineHeight: '20px',
        letterSpacing: '-0.2px'
    }
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={spanStyle}>{label}</span>
            <span style={{ flexGrow: 1, borderBottom: '1px dashed var(--neutral-200)', margin: '0px 8px 8px 8px' }} />
            <span style={spanStyle}>
                {currency}
                {FormatNumberFuncs.formatNumber(value, 2)}
            </span>
        </div>
    )
}
